import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ResendVerification.css';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './shared';

/**
 * Issue #15: Resend Verification Email page
 * - When navigated from Register success screen: email is passed as prop, no email input shown
 * - When navigated directly: email input form is shown
 * - Max 3 resend attempts, then button is deactivated (Scenario 4)
 * - Shows success text after each successful resend (Scenario 3)
 * - Shows customer service contact text
 */
const ResendVerification = ({ email: emailProp, onNavigateToRegister, onNavigateToLogin }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState(emailProp || '');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resendCount, setResendCount] = useState(0);

  const hasEmailFromRegistration = !!emailProp;

  useEffect(() => {
    const appEl = document.querySelector('.App');
    if (appEl) appEl.style.background = '#ffffff';
    document.body.style.backgroundColor = '#ffffff';
    return () => {
      if (appEl) appEl.style.background = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  const validateEmail = (emailToValidate) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToValidate)) {
      return false;
    }

    // Check university domain whitelist
    const universityDomains = [
      // Hamburg Universities - HIGH Confidence
      'studium.uni-hamburg.de',        // Universität Hamburg
      'tuhh.de',                        // TU Hamburg
      'hcu-hamburg.de',                 // HafenCity Universität
      'haw-hamburg.de',                 // HAW Hamburg
      'law-school.de',                  // Bucerius Law School
      'hfbk-hamburg.de',               // HfBK Hamburg
      'hfbk.hamburg.de',               // HfBK Hamburg (alternative)

      // Hamburg Universities - MEDIUM-HIGH Confidence
      'hsu-hh.de',                      // Helmut-Schmidt-Universität
      'the-klu.org',                    // Kühne Logistics University
      'hfmt-hamburg.de',                // Hochschule für Musik und Theater
      'muthesius.de',                   // Muthesius Kunsthochschule

      // Schleswig-Holstein Universities - HIGH Confidence
      'mail.uni-kiel.de',               // CAU Kiel (default format)
      'stu.uni-kiel.de',                // CAU Kiel (alternative format)
      'stu.haw-kiel.de',                // HAW Kiel (current)
      'student.fh-kiel.de',             // HAW Kiel (legacy)
      'stud.th-luebeck.de',             // TH Lübeck
      'stud.hs-flensburg.de',           // Hochschule Flensburg
      'mh-luebeck.de',                  // Musikhochschule Lübeck

      // HTW Berlin (for testing)
      'htwberlin.de',                   // HTW Berlin
      'student.htwberlin.de',           // HTW Berlin (student subdomain)

      // Legacy/Other domains
      'student.uni-kiel.de',
      'student.uni-hamburg.de',
      'student.tu-berlin.de',
      'student.lmu.de',
      'student.tum.de',
      'student.rwth-aachen.de',
      'student.uni-heidelberg.de',
      'student.uni-bonn.de',
      'student.uni-muenchen.de',

      // Testing
      'test-uni.de',
      'universe-app.de',
      'von-sulecki.de',
      'universe-app.online'
    ];

    return universityDomains.some(domain => emailToValidate.toLowerCase().endsWith(`@${domain}`));
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors({});
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = t('auth.emailRequired');
    } else if (!validateEmail(email)) {
      newErrors.email = t('auth.invalidEmail');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResend = async (e) => {
    if (e) e.preventDefault();

    // Skip form validation when email comes from registration
    if (!hasEmailFromRegistration && !validateForm()) {
      return;
    }

    if (resendCount >= 3) return;

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await axios.post('/api/resend-verification', {
        email: hasEmailFromRegistration ? emailProp : email
      });

      if (response.data.success) {
        setSuccessMessage(t('auth.verificationLinkSentSuccess'));
        setResendCount(prev => prev + 1);
      }
    } catch (error) {
      if (error.response && error.response.status === 429) {
        setResendCount(3);
        setErrors({ general: error.response.data.message });
      } else if (error.response && error.response.data) {
        const { message, field } = error.response.data;
        if (field) {
          setErrors({ [field]: message });
        } else {
          setErrors({ general: message });
        }
      } else {
        setErrors({ general: t('auth.systemUnavailable') });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled = isSubmitting || resendCount >= 3 || (!hasEmailFromRegistration && !email.trim());

  return (
    <div className="resend-verification-container">
      {/* Navigation Bar */}
      <header className="navigation-bar" role="banner">
        <h1 className="page-title">{t('auth.resendVerification')}</h1>
      </header>

      {/* Main Content */}
      <main className="main-content" role="main">
        <form onSubmit={handleResend} aria-label={t('auth.resendVerification')}>
          {/* Email input: only when no email from registration */}
          {!hasEmailFromRegistration && (
            <>
              <div className="text-field-wrapper">
                <label htmlFor="resend-email" className="field-label">
                  {t('auth.emailLabel')}
                </label>
                <input
                  type="email"
                  id="resend-email"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  placeholder={t('auth.studentEmail')}
                  className={`text-field-input-full ${errors.email ? 'error' : ''}`}
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error help-text' : 'help-text'}
                  required
                  autoComplete="email"
                />
                {errors.email && (
                  <div id="email-error" className="error-message" role="alert">
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Help Text */}
              <div id="help-text" className="help-text" role="status">
                {t('auth.resendHelpText')}
              </div>
            </>
          )}

          {/* General Error */}
          {errors.general && (
            <div className="error-message general-error" role="alert" aria-live="polite">
              {errors.general}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="success-box" role="status" aria-live="polite">
              <div className="success-message">
                {successMessage}
              </div>
            </div>
          )}

          {/* Issue #15 Scenario 2: Resend Button — Bug #200: hide after successful resend */}
          {resendCount < 1 && (
            <div className="submit-button-wrapper">
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={isButtonDisabled}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? t('auth.sending') : t('auth.resendVerificationButton')}
              </Button>
            </div>
          )}

          {/* Bug #201: Show "Alles klar" button after successful resend */}
          {resendCount >= 1 && onNavigateToLogin && (
            <div className="verification-sent-button-wrapper">
              <Button
                variant="primary"
                fullWidth
                onClick={onNavigateToLogin}
              >
                {t('auth.allesKlar')}
              </Button>
            </div>
          )}

          {/* Issue #15: Customer service text */}
          <div className="customer-service-notice" role="note">
            <p>{t('auth.customerServiceNotice')}<span className="customer-service-email">{t('auth.customerServiceEmail')}</span></p>
          </div>

        </form>
      </main>
    </div>
  );
};

export default ResendVerification;
